<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const faq = [
  {
    q: 'Apakah ketiganya saling mengurangi satu sama lain (mis. voucher dihitung dari total setelah dipotong promo)?',
    a: 'Tidak. Promo, Voucher, dan Poin semuanya dihitung dari subtotal belanja yang SAMA, lalu dijumlahkan menjadi satu total potongan di akhir. Tidak ada yang dihitung dari sisa setelah potongan lain — jadi urutan mana yang "diterapkan duluan" tidak memengaruhi hasil akhirnya.',
  },
  {
    q: 'Kalau salah satu (voucher atau poin) ternyata gagal saat transaksi disimpan, apa yang terjadi ke bagian lain?',
    a: 'Seluruh transaksi dibatalkan (tidak ada yang tersimpan sama sekali) — bukan hanya bagian yang gagal. Ini untuk mencegah situasi transaksi tersimpan dengan diskon yang tidak lengkap/tidak sesuai. Kasir tinggal ulangi checkout, biasanya tanpa kode/poin yang bermasalah.',
  },
  {
    q: 'Apakah ada batas berapa banyak promo yang bisa dipakai bersamaan?',
    a: 'Tidak ada batas jumlah untuk promo Auto-Apply — semua promo otomatis yang syaratnya terpenuhi akan diterapkan bersamaan. Untuk Voucher, hanya SATU kode voucher yang bisa dipakai per transaksi.',
  },
  {
    q: 'Total potongan bisa lebih besar dari total belanja?',
    a: 'Tidak bisa. Sistem otomatis membatasi total gabungan semua potongan (promo + voucher + poin) supaya tidak pernah melebihi subtotal belanja itu sendiri — total transaksi tidak akan pernah menjadi minus.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon
            icon="ri-arrow-right-s-line"
            size="16"
            class="text-medium-emphasis"
          />
          <span class="text-body-2">Gabungan Promo + Voucher + Poin</span>
        </div>
        <RouterLink to="/userguide">
          <VBtn
            variant="text"
            prepend-icon="ri-arrow-left-line"
            size="small"
            class="mb-4"
          >
            Kembali ke Daftar Modul
          </VBtn>
        </RouterLink>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar
            color="primary"
            variant="tonal"
            size="64"
            rounded="lg"
            class="me-4"
          >
            <VIcon
              icon="ri-git-merge-line"
              size="36"
            />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">
              Pakai Promo + Voucher + Poin Sekaligus
            </h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Ketiganya bisa digabung dalam satu transaksi yang sama — begini caranya
            </p>
          </div>
        </div>
        <VAlert
          type="info"
          variant="tonal"
        >
          <strong>Tujuan halaman ini:</strong>
          Menjelaskan bahwa Promo (otomatis), Voucher (kode), dan Penukaran Poin BISA dipakai
          bersamaan dalam satu transaksi kasir, serta bagaimana potongannya dihitung dan
          ditampilkan di struk.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-book-open-line"
              class="me-2"
              color="primary"
            />
            Bisakah Ketiganya Dipakai Bersamaan?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Bisa.</strong> PintuKasir memang dirancang supaya Promo (otomatis/manual),
              Voucher (kode), dan Penukaran Poin bisa aktif <strong>sekaligus dalam satu transaksi
                yang sama</strong> — Anda tidak perlu memilih hanya salah satu.
            </p>
            <VAlert
              type="success"
              variant="tonal"
              class="mb-0"
            >
              <strong>Contoh nyata:</strong> Seorang pelanggan tetap datang belanja Rp150.000.
              Toko Anda sedang punya promo otomatis "Diskon 5% Seluruh Order", pelanggan itu
              juga punya kode voucher "ULTAH20" dari giveaway Instagram, dan dia punya 200 poin
              yang ingin ditukar. Kasir bisa menerapkan <strong>ketiganya sekaligus</strong> di
              satu transaksi yang sama — pelanggan mendapat potongan gabungan dari promo, voucher,
              dan poin, semuanya dalam satu kali checkout.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Pakai -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-play-circle-line"
              class="me-2"
              color="success"
            />
            Cara Menggabungkan Ketiganya di Layar Kasir
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-0">
              <li class="mb-3">
                Masukkan semua item pelanggan ke keranjang seperti biasa di layar
                <strong>POS Kasir</strong>.
              </li>
              <li class="mb-3">
                <strong>Promo otomatis</strong> (kalau ada yang syaratnya terpenuhi) akan langsung
                muncul sendiri di bagian "Promo Aktif" — tidak perlu tindakan apa pun. Kalau ada
                promo manual yang ingin diaktifkan juga, klik chip-nya.
              </li>
              <li class="mb-3">
                Kalau pelanggan punya <strong>kode voucher</strong>, ketik di kolom "Kode voucher"
                lalu klik "Cek" — lihat panduan
                <RouterLink to="/userguide/promo-voucher-loyalty/voucher">
                  Voucher &amp; Kupon
                </RouterLink>
                untuk detailnya.
              </li>
              <li class="mb-3">
                Kalau pelanggan ingin <strong>menukar poin</strong>, pastikan dulu transaksi
                terhubung ke data Customer-nya, lalu isi kolom "Tukar poin" — lihat panduan
                <RouterLink to="/userguide/promo-voucher-loyalty/loyalty">
                  Program Loyalty
                </RouterLink>
                untuk detailnya.
              </li>
              <li class="mb-0">
                Cek ringkasan belanja di panel kanan — setiap sumber potongan tampil sebagai
                <strong>baris terpisah</strong> (Diskon Promo, Diskon Voucher, Potongan Poin),
                lalu klik <strong>"Bayar"</strong> seperti biasa untuk menyelesaikan transaksi.
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Hitung -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-calculator-line"
              class="me-2"
              color="info"
            />
            Bagaimana Cara Sistem Menghitung Totalnya?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Semua potongan dihitung dari subtotal belanja yang sama, lalu dijumlahkan menjadi
              satu total potongan sebelum pajak:
            </p>
            <VCard
              variant="outlined"
              class="mb-3"
            >
              <VCardText>
                <VTable density="compact">
                  <tbody>
                    <tr>
                      <td class="text-body-2">
                        Subtotal Belanja
                      </td>
                      <td class="text-body-2 text-end">
                        Rp150.000
                      </td>
                    </tr>
                    <tr>
                      <td class="text-body-2">
                        – Diskon Promo (5%)
                      </td>
                      <td class="text-body-2 text-end text-error">
                        – Rp7.500
                      </td>
                    </tr>
                    <tr>
                      <td class="text-body-2">
                        – Diskon Voucher "ULTAH20"
                      </td>
                      <td class="text-body-2 text-end text-error">
                        – Rp20.000
                      </td>
                    </tr>
                    <tr>
                      <td class="text-body-2">
                        – Potongan 200 Poin
                      </td>
                      <td class="text-body-2 text-end text-error">
                        – Rp20.000
                      </td>
                    </tr>
                    <tr>
                      <td class="text-body-2 font-weight-medium">
                        Setelah Diskon
                      </td>
                      <td class="text-body-2 text-end font-weight-medium">
                        Rp102.500
                      </td>
                    </tr>
                    <tr>
                      <td class="text-body-2">
                        + Pajak
                      </td>
                      <td class="text-body-2 text-end">
                        sesuai % pajak toko
                      </td>
                    </tr>
                    <tr>
                      <td class="text-body-1 font-weight-bold">
                        Total Dibayar
                      </td>
                      <td class="text-body-1 text-end font-weight-bold">
                        Rp102.500 + pajak
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </VCardText>
            </VCard>
            <p class="text-body-2 mb-0">
              Ketiga potongan (promo, voucher, poin) sama-sama dihitung dari <strong>Rp150.000</strong>
              (bukan dari sisa setelah potongan lain), lalu ketiganya dijumlahkan sekaligus. Struk
              yang dicetak/dikirim ke pelanggan menampilkan setiap sumber potongan sebagai baris
              terpisah, bukan digabung jadi satu angka "Diskon" saja — supaya pelanggan dan Anda
              sama-sama jelas potongan itu berasal dari mana.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Catatan penting -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VAlert
          type="warning"
          variant="tonal"
          icon="ri-error-warning-line"
        >
          <strong>⚠️ PENTING:</strong>
          Kalau kode voucher atau penukaran poin ternyata gagal di detik-detik terakhir (mis.
          kuota voucher baru saja habis, atau saldo poin ternyata sudah dipakai duluan di
          transaksi lain), <strong>seluruh transaksi otomatis dibatalkan</strong> — bukan hanya
          bagian yang bermasalah. Tidak ada transaksi yang tersimpan dengan diskon setengah-setengah.
          Kasir cukup coba ulang checkout tanpa kode/poin yang bermasalah tersebut.
        </VAlert>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          ❓ Pertanyaan yang Sering Ditanyakan
        </h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel
            v-for="(item, i) in faq"
            :key="i"
          >
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">
                {{ item.a }}
              </p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Promo & Diskon', sub: 'Cara membuat & mengatur promo', icon: 'ri-price-tag-3-line', color: 'primary', to: '/userguide/promo-voucher-loyalty/promo' },
              { title: 'Voucher & Kupon', sub: 'Cara membuat & memakai kode voucher', icon: 'ri-coupon-3-line', color: 'info', to: '/userguide/promo-voucher-loyalty/voucher' },
              { title: 'Program Loyalty', sub: 'Poin & stempel untuk pelanggan setia', icon: 'ri-vip-diamond-line', color: 'secondary', to: '/userguide/promo-voucher-loyalty/loyalty' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink
              :to="link.to"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar
                    :color="link.color"
                    variant="tonal"
                    size="40"
                    rounded
                  >
                    <VIcon
                      :icon="link.icon"
                      size="22"
                    />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">
                      {{ link.title }}
                    </p>
                    <p class="text-caption text-medium-emphasis mb-0">
                      {{ link.sub }}
                    </p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
